import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../shared/constants/app.constant';
import { isApiError } from '../../../shared/services/http.service';
import { authKeys } from '../constants/auth.constant';
import type { Me } from '../models/auth.model';
import { authService } from '../services/auth.service';

export interface SessionState {
  readonly user: Me | null;
  readonly isLoading: boolean;
  readonly isUnauthenticated: boolean;
  readonly error: Error | null;
  readonly retry: () => void;
}

export function useSession(): SessionState {
  const query = useQuery({
    queryKey: authKeys.me,
    queryFn: authService.me,
    retry: (count, error) => !(isApiError(error) && error.status === 401) && count < 2,
    staleTime: 60_000,
  });
  const isUnauthenticated = isApiError(query.error) && query.error.status === 401;
  return {
    user: query.data?.user ?? null,
    isLoading: query.isPending,
    isUnauthenticated,
    error: isUnauthenticated ? null : query.error,
    retry: () => void query.refetch(),
  };
}

export function useLogout(): { logout: () => void; isPending: boolean } {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: authService.logout,
    onSettled: () => {
      queryClient.clear();
      void navigate(ROUTES.login, { replace: true });
    },
  });
  const logout = useCallback(() => mutation.mutate(), [mutation]);
  return { logout, isPending: mutation.isPending };
}
